/**
 * Shared preview-link helper.
 *
 * Mints a short-lived (15m) preview JWT via the backend and opens the
 * artist-website preview route in a new tab. The preview route bypasses
 * the approval/publish gate so a super-admin can inspect a pending or
 * rejected site before deciding to approve/reject.
 *
 * - Domain-admins: the backend always issues a token for their own domain;
 *   any `slug` argument is ignored server-side (IDOR guard).
 * - Super-admins: pass the target `slug` to preview any tenant.
 *
 * Reused by DomainManager, SuperAdminDashboard, CompletionScreen, and
 * VerifiedScreen so the URL-building logic lives in one place.
 */
import axios from '../utils/axiosInstance';

const WEBSITE_BASE =
  import.meta.env.VITE_WEBSITE_URL || 'http://localhost:3000';

export interface PreviewTokenResponse {
  token: string;
  slug: string;
}

/**
 * Fetch a preview token for the given slug (or the caller's own domain
 * when omitted). Returns the token + the resolved slug.
 */
export async function fetchPreviewToken(
  slug?: string,
): Promise<PreviewTokenResponse> {
  const params = slug ? { slug } : undefined;
  const { data } = await axios.get<PreviewTokenResponse>(
    '/api/auth/preview-token',
    { params },
  );
  return data;
}

/**
 * Build the public-website preview URL for a slug + token.
 * Mirrors the construction in CompletionScreen.tsx so the two stay in sync.
 */
export function buildPreviewUrl(slug: string, token: string): string {
  const encodedSlug = encodeURIComponent(slug);
  const encodedToken = encodeURIComponent(token);
  try {
    const baseUrl = new URL(WEBSITE_BASE);
    return `${baseUrl.protocol}//${encodedSlug}.${baseUrl.host}/artist/preview/${encodedSlug}?token=${encodedToken}`;
  } catch {
    // Fallback if WEBSITE_BASE is malformed.
    return `${WEBSITE_BASE}/artist/preview/${encodedSlug}?token=${encodedToken}`;
  }
}

/**
 * Mint a preview token and open the preview in a new tab.
 * Non-fatal: swallows errors so callers don't need try/catch.
 */
export async function openPreview(slug?: string): Promise<void> {
  const { token, slug: resolvedSlug } = await fetchPreviewToken(slug);
  const url = buildPreviewUrl(resolvedSlug, token);
  window.open(url, '_blank', 'noopener,noreferrer');
}
